import React from "react"
import { useTranslation } from "react-i18next"

import { Action } from "shared/types/Action"
import { Room } from "shared/types/Room"
import { RoomPrice } from "shared/types/__generated__/graphql"
import { useNumberFormat } from "shared/ui/input/number/hooks/useNumberFormat"
import { Readonly } from "shared/ui/readonly/Readonly"

import { RoomFormModel } from "features/room-management/types/RoomFormModel"
import { RoomFormTabType } from "features/room-management/types/RoomFormTabType"
import { ProjectPageSidebarActions } from "pages/project-page/ui/ProjectPageSidebarActions"
import { PriceTag } from "widgets/price-tag/PriceTag"
import { RoomForm } from "widgets/room/room-form/RoomForm"
import "widgets/room/room-form/styles/_room-form.scss"

import "../../styles/_project-page-sidebar.scss"

export interface ProjectPageRoomSidebarViewModel {
  id: string

  room: Room
  roomFormModel: RoomFormModel

  isReadonly?: boolean
  isDisabled?: boolean

  hideControls?: boolean
  actions: Action[]
  autoFocus?: boolean

  productTotal: number
  price?: RoomPrice | null
  isLoadingPrice?: boolean

  sidebarActiveTab: string | null
  setSidebarActiveTab: (tab: string | null) => void
}

const SIDEBAR_HIDDEN_TABS: RoomFormTabType[] = [RoomFormTabType.GENERAL]

interface RoomSidebarHeaderProps {
  room: Room
  isReadonly?: boolean
  price?: RoomPrice | null
  productTotal: number
  isLoadingPrice?: boolean
}

const RoomSidebarHeader = React.memo(
  ({
    room,
    isReadonly,
    price,
    productTotal,
    isLoadingPrice
  }: RoomSidebarHeaderProps) => {
    const { t } = useTranslation()
    const formattedProductTotal = useNumberFormat(productTotal)

    if (isReadonly) {
      return (
        <div className="room-sidebar__header">
          <Readonly.Root className="room-sidebar__title">
            <Readonly.Label>{t("Room.Room")}</Readonly.Label>
            <Readonly.Value>
              <RoomForm.Title room={room} showClarificationRequestIndicator />
            </Readonly.Value>
          </Readonly.Root>

          <PriceTag
            price={price?.priceTotal}
            currency={price?.currency}
            isLoading={isLoadingPrice}
            isComplete={price?.isComplete}
            label={t("Price.TotalPrice")}
            className="room-sidebar__price"
            data-testid="room__price"
          />
          <Readonly.Root className="room-sidebar__product-total">
            <Readonly.Label>{t("Project.ProductTotal")}</Readonly.Label>
            <Readonly.Value>{formattedProductTotal}</Readonly.Value>
          </Readonly.Root>
        </div>
      )
    }

    return (
      <div className="room-sidebar__header">
        <RoomForm.NumberInput />
        <RoomForm.LabelInput />
        <RoomForm.FloorInput />
        <RoomForm.PositionInput />
      </div>
    )
  }
)

export const ProjectPageRoomSidebarView = React.memo(
  ({
    id,
    room,
    isReadonly,
    isDisabled,
    roomFormModel,
    actions,
    autoFocus,
    hideControls,
    price,
    productTotal,
    isLoadingPrice,
    sidebarActiveTab,
    setSidebarActiveTab
  }: ProjectPageRoomSidebarViewModel) => {
    return (
      <div
        className="project-page-sidebar project-page-sidebar--room"
        data-testid="project-sidebar"
      >
        <RoomForm
          id={id}
          {...roomFormModel}
          isReadonly={isReadonly}
          isDisabled={isDisabled}
          className="project-page-sidebar__content"
        >
          <RoomSidebarHeader
            room={room}
            isReadonly={isReadonly}
            price={price}
            productTotal={productTotal}
            isLoadingPrice={isLoadingPrice}
          />
          <RoomForm.Tabs
            hiddenTabs={SIDEBAR_HIDDEN_TABS}
            activeTab={
              (sidebarActiveTab as RoomFormTabType | null) ?? undefined
            }
            onTabChange={setSidebarActiveTab}
          />
        </RoomForm>

        {hideControls ? null : (
          <ProjectPageSidebarActions
            actions={actions}
            autoFocus={autoFocus}
            isIconOnly={isReadonly}
          />
        )}
      </div>
    )
  }
)
